import React, { useEffect, useState } from 'react';
import { Shield, FileText, Clock, Lock, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export default function TrustSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const logos = [{
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8X52QHFHr2pezRgSGP8PRu/Medco.png',
    alt: 'MedCo',
    href: 'https://www.medco.org.uk/',
    caption: 'MedCo Accredited Medical Expert – Psychological Injury Assessments'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bdKLxaE25XhPcbz47Q5ek2/GMC.png',
    alt: 'GMC',
    href: 'https://www.gmc-uk.org/',
    caption: 'GMC Registered Psychiatrist – Member of the General Medical Council'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oejz6MBWaYPHLboKrsxoJ8/ICO.png',
    alt: 'ICO',
    href: 'https://ico.org.uk/',
    caption: 'ICO Registered – Data Protection & Confidentiality Compliance'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9akzzny18c2cokdDbeaSwa/RCPsych.png',
    alt: 'Royal College of Psychiatrists',
    href: 'https://www.rcpsych.ac.uk/',
    caption: 'Member of the Royal College of Psychiatrists – Trusted UK Psychiatric Authority'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/r7LQZ1MUtT9MjPyaNm53VY/Bond_Solon.jpg',
    alt: 'Bond Solon',
    href: 'https://www.bondsolon.com/',
    caption: 'Certified Expert Witness – Bond Solon Accredited'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6MiZXdLANurvspjz8K3bmS/medical_school_council.png',
    alt: 'Medical School Council',
    href: 'https://www.medschools.ac.uk/',
    caption: 'Recognised by the Medical Schools Council – Upholding UK Clinical Standards'
  }];
  // Auto-scroll every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 2000);
    return () => clearInterval(interval);
  }, [currentIndex]);
  const totalSlides = Math.ceil(logos.length / 3);
  const nextSlide = () => {
    setCurrentIndex(prevIndex => (prevIndex + 1) % totalSlides);
  };
  const prevSlide = () => {
    setCurrentIndex(prevIndex => (prevIndex - 1 + totalSlides) % totalSlides);
  };
  const visibleLogos = logos.slice(currentIndex * 3, currentIndex * 3 + 3);
  return <section className="py-16 bg-gray-50" data-id="element-227">
      <div className="container mx-auto px-4" data-id="element-228">
        <h2 className="text-3xl font-serif font-bold text-primary text-center mb-12" data-id="element-229">
          Why Legal Professionals Trust Medicolegal Psychiatry
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-230">
          {/* Robust & Objective */}
          <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-primary" data-id="element-231">
            <div className="text-primary mb-4" data-id="element-232">
              <Shield className="h-10 w-10" data-id="element-233" />
            </div>
            <h3 className="text-xl font-serif font-bold text-primary mb-2" data-id="element-234">
              Robust & Objective
            </h3>
            <p className="text-slate" data-id="element-235">
              Evidence-based, impartial reports, defensible in court. Our
              reports are built on a foundation of sound clinical practice and
              objective evidence.
            </p>
          </div>
          {/* Clear & Concise */}
          <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-primary" data-id="element-236">
            <div className="text-primary mb-4" data-id="element-237">
              <FileText className="h-10 w-10" data-id="element-238" />
            </div>
            <h3 className="text-xl font-serif font-bold text-primary mb-2" data-id="element-239">
              Clear & Concise
            </h3>
            <p className="text-slate" data-id="element-240">
              Complex psychiatric issues translated into accessible language.
              Every report directly addresses the legal questions posed,
              avoiding unnecessary jargon.
            </p>
          </div>
          {/* Timely & Responsive */}
          <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-primary" data-id="element-241">
            <div className="text-primary mb-4" data-id="element-242">
              <Clock className="h-10 w-10" data-id="element-243" />
            </div>
            <h3 className="text-xl font-serif font-bold text-primary mb-2" data-id="element-244">
              Timely & Responsive
            </h3>
            <p className="text-slate" data-id="element-245">
              Reports delivered within 10 working days, expedited options
              available. We are committed to meeting deadlines with prompt and
              efficient service.
            </p>
          </div>
          {/* Ethical & Confidential */}
          <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-primary" data-id="element-246">
            <div className="text-primary mb-4" data-id="element-247">
              <Lock className="h-10 w-10" data-id="element-248" />
            </div>
            <h3 className="text-xl font-serif font-bold text-primary mb-2" data-id="element-249">
              Ethical & Confidential
            </h3>
            <p className="text-slate" data-id="element-250">
              Strict adherence to professional and legal standards. We handle
              all cases with the utmost discretion and integrity.
            </p>
          </div>
        </div>
        {/* Trust Logos */}
        <div className="mt-16" data-id="element-251">
          <h3 className="text-xl font-serif font-bold text-primary text-center mb-6" data-id="element-252">
            Our Accreditations & Memberships
          </h3>
          <div className="relative" data-id="element-253">
            <div className="bg-gradient-to-b from-primary to-primary-dark p-8 rounded-lg shadow-lg" data-id="element-254">
              <div className="flex justify-center items-center mb-6" data-id="element-255">
                <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white text-primary rounded-full shadow-md hover:bg-gray-100 transition-colors z-10" aria-label="Previous logos" data-id="element-256">
                  <ChevronLeft className="h-6 w-6" data-id="element-257" />
                </button>
                <div className="flex justify-center items-center gap-8 overflow-hidden w-full max-w-5xl mx-auto px-10" data-id="element-258">
                  <AnimatePresence mode="wait" data-id="element-259">
                    <motion.div key={currentIndex} initial={{
                    opacity: 0,
                    x: 50
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -50
                  }} transition={{
                    duration: 0.3
                  }} className="flex justify-center items-start gap-16 w-full" data-id="element-260">
                      {visibleLogos.map((logo, index) => <div key={index} className="flex flex-col items-center justify-center" data-id="element-261">
                          <a href={logo.href} target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-80 flex items-center justify-center bg-white p-4 rounded-lg mb-3" data-id="element-262">
                            <img src={logo.src} alt={logo.alt} className="h-[70px]" style={{
                          height: '70px',
                          objectFit: 'contain'
                        }} data-id="element-263" />
                          </a>
                          <p className="text-white text-center text-sm font-sans max-w-[200px]" data-id="element-264">
                            {logo.caption}
                          </p>
                        </div>)}
                    </motion.div>
                  </AnimatePresence>
                </div>
                <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white text-primary rounded-full shadow-md hover:bg-gray-100 transition-colors z-10" aria-label="Next logos" data-id="element-265">
                  <ChevronRight className="h-6 w-6" data-id="element-266" />
                </button>
              </div>
              {/* Pagination dots */}
              <div className="flex justify-center gap-2 mt-4" data-id="element-267">
                {Array.from({
                length: totalSlides
              }).map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} className={`h-2 w-2 rounded-full ${currentIndex === index ? 'bg-white' : 'bg-white/40'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-268" />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}